import FIRST_NAMES from '../variables/data/firstNames';
import LAST_NAMES from '../variables/data/lastNames';
import COMPANY_NAMES from '../variables/data/companyNames';
import ADDRESSES from '../variables/data/addresses';
import VEHICLE_NUMBERS from '../variables/data/vehicleNumbers';
// Fields to mask by type.
const STRING_FIELDS = new Set([
  'firstName',
  'lastName',
  'companyName',
  'email',
  'primaryEmail',
  'signUpEmail',
  'payorEmail',
  'payorEmailAddress',
  'phone',
  'mobile',
  'primaryPhone',
  'whatsappPhoneNumber',
  'phoneNumber',
  'name',
  'requestedByEmail',
  'patientName',
  'jointName',
  'clientName',
  'agent',
  'principalDriver',
]);
const DATE_FIELDS = new Set(['dob', 'dateOfBirth', 'birthDate']);
const ADDRESS_FIELDS = new Set([
  'addressLine1',
  'addressLine2',
  'addressLine3',
  'insuredItemLocation',
  'address',
  'locationOfLoss',
  'incidentLocation',
  'address1',
  'address2',
  'address3',
]);
const VEHICLE_NUMBER_FIELDS = new Set([
  'registrationNumber',
  'engineNumber',
  'chassisNumber',
]);

// --- Masking logic ---
export function anonymizePayload(payload, subjectId) {
  const secretKey = 'KkjN2Ih5S6mkK5Gl';
  const rootSeed = hashSeed(secretKey, subjectId || 'seed');
  return walk(payload, secretKey, rootSeed);
}

function walk(value, secretKey, seed, parentKey) {
  if (Array.isArray(value)) {
    // Each item gets its own derived seed to keep items stable but distinct.
    return value.map(item => walk(item, secretKey, seed, parentKey));
  }
  if (!isObject(value)) {
    return value; // primitives pass through untouched
  }

  const nextSeed = seed;
  const output = {};

  for (const [key, val] of Object.entries(value)) {
    if (val == null) {
      output[key] = val; // keep null/undefined as-is
      // eslint-disable-next-line no-continue
      continue;
    }

    if (parentKey === 'phones' && key === 'value') {
      output[key] = maskString('phone', nextSeed);
      // eslint-disable-next-line no-continue
      continue;
    }

    if (STRING_FIELDS.has(key) && typeof val === 'string') {
      output[key] = maskString(key, nextSeed);
      // eslint-disable-next-line no-continue
      continue;
    }

    if (DATE_FIELDS.has(key)) {
      output[key] = maskDate(val, nextSeed);
      // eslint-disable-next-line no-continue
      continue;
    }

    if (ADDRESS_FIELDS.has(key) && typeof val === 'string') {
      output[key] = maskAddressField(nextSeed);
      // eslint-disable-next-line no-continue
      continue;
    }
    if (VEHICLE_NUMBER_FIELDS.has(key) && typeof val === 'string') {
      output[key] = maskVehicleNumberField(nextSeed);
      // eslint-disable-next-line no-continue
      continue;
    }

    // Recurse into nested objects/arrays.
    output[key] = walk(val, secretKey, nextSeed, key);
  }

  return output;
}

function maskString(field, seed) {
  if (field === 'firstName') {
    const index = hexToInt(seed, 2) % FIRST_NAMES.length;
    return `TestFirst_${index}`; // pickFromList(FIRST_NAMES, seed);
  }
  if (
    field === 'name' ||
    field === 'jointName' ||
    field === 'clientName' ||
    field === 'agent' ||
    field === 'principalDriver'
  ) {
    const index = hexToInt(seed, 2) % FIRST_NAMES.length;
    return `TestFull_${index}`;
  }
  if (field === 'lastName') {
    const index = hexToInt(seed, 2) % LAST_NAMES.length;
    return `TestLast_${index}`; // pickFromList(LAST_NAMES, seed);
  }
  if (field.toLowerCase().includes('email')) {
    return `user.${seed.slice(0, 8)}@example.test`;
  }
  if (field.toLowerCase().includes('phone') || field === 'mobile') {
    return buildPhone(seed);
  }
  if (field === 'companyName') {
    return pickFromList(COMPANY_NAMES, seed);
  }
  if (field.toLowerCase().includes('address')) {
    return pickFromList(ADDRESS_FIELDS, seed);
  }
  return 'Masked';
}

function maskDate(value, seed) {
  // Stable DOB-like date (keeps age ranges plausible).
  const baseYear = 1970;
  const yearSpan = 30;
  const month = (hexToInt(seed, 2) % 12) + 1;
  const day = (hexToInt(seed, 4) % 28) + 1;
  const year = baseYear + (hexToInt(seed, 6) % yearSpan);

  const iso = `${year}-${pad(month)}-${pad(day)}T00:00:00.000Z`;
  const date = new Date(iso);
  return value instanceof Date ? date : date.toISOString();
}

function maskAddressField(seed) {
  return pickFromList(ADDRESSES, seed);
}

function maskVehicleNumberField(seed) {
  return pickFromList(VEHICLE_NUMBERS, seed);
}

function pickFromList(list, seed) {
  return list[hexToInt(seed, 2) % list.length];
}

function buildPhone(seed) {
  // Produce a 10-digit, deterministic phone-like string.
  const digits = seed.replace(/[^0-9a-f]/gi, '').slice(0, 10);
  const normalized = digits.padEnd(10, '0');
  return normalized.replace(/[a-f]/gi, '7');
}

// --- Small utilities ---
function isObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value);
}

function hashSeed(secretKey, input) {
  // Deterministic hash used for masking; avoids Node-only crypto in the browser.
  const base = `${secretKey}|${input}`;
  return ['1', '2', '3', '4']
    .map(salt =>
      fnv1a(`${salt}|${base}`)
        .toString(16)
        .padStart(8, '0'),
    )
    .join('');
}

function fnv1a(value) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function hexToInt(hex, chars) {
  return parseInt(hex.slice(0, chars), 16);
}

function pad(num) {
  return String(num).padStart(2, '0');
}

function readInput(inputPath, fsModule, pathModule) {
  if (inputPath) {
    const fullPath = pathModule.resolve(inputPath);
    return fsModule.readFileSync(fullPath, 'utf8');
  }
  // If no --input provided, read stdin.
  return fsModule.readFileSync(0, 'utf8');
}
